import { z } from "zod";
import type { WebMcpCopy } from "../content/entries/webmcp";
import { WEBMCP_ACTIONS, webMcpToolName, type WebMcpAction, type WebMcpRisk } from "./manifest";
import type { WebMcpBinding, WebMcpResult } from "./types";

export type NativeTool = {
  name: string; description: string; inputSchema: Record<string, unknown>;
  annotations: { readOnlyHint: boolean; consequentialHint: boolean };
  // Chrome 152 can invoke this callback with only `input`, despite the draft
  // context shape. `signal` is therefore intentionally optional at this edge.
  execute: (input: unknown, context?: { signal?: unknown }) => Promise<unknown>;
};
export type ModelContext = {
  registerTool: (tool: NativeTool, options: { signal: AbortSignal }) => Promise<void> | void;
};
export type ConfirmAction = (description: string, details: string, signal: AbortSignal) => Promise<boolean>;
export type WebMcpObservation = { action: WebMcpAction; risk: WebMcpRisk; outcome: WebMcpResult["status"]; duration: "lt_1s" | "1_to_10s" | "over_10s" };
export type WebMcpActionPolicy = (action: WebMcpAction) => boolean;

function isThenable(value: WebMcpResult | PromiseLike<WebMcpResult>): value is PromiseLike<WebMcpResult> {
  return typeof (value as PromiseLike<WebMcpResult>).then === "function";
}

function callbackSignal(context: unknown): AbortSignal | undefined {
  try {
    const signal = context && typeof context === "object" ? (context as { signal?: unknown }).signal : undefined;
    return signal instanceof AbortSignal ? signal : undefined;
  } catch {
    return undefined;
  }
}

/** Owns only our tools. Never clears another provider's model context. */
export class WebMcpRuntime {
  private registrations = new Map<string, () => void>();
  private pending = new Map<string, Promise<unknown>>();
  private busy = false;
  private disposed = false;
  constructor(private model: ModelContext, private copy: WebMcpCopy, private route: string,
    private confirm: ConfirmAction, private isCurrent: () => boolean = () => true,
    private observe: (event: WebMcpObservation) => void = () => {},
    private isActionAllowed: WebMcpActionPolicy = () => true) {}

  private actionAllowed(action: WebMcpAction) {
    try { return this.isActionAllowed(action); }
    catch { return false; }
  }

  bind(binding: WebMcpBinding): () => void {
    if (this.disposed || binding.enabled === false || !this.actionAllowed(binding.action)) return () => {};
    let name: string;
    try { name = webMcpToolName(binding.action, binding.instance); }
    catch { return () => {}; } // Optional agent integration must never crash the human UI.
    // A collision is a programming error, not permission to replace another component.
    if (this.registrations.has(name)) return () => {};
    const lifetime = new AbortController();
    const nativeRegistration = new AbortController();
    let deliveringTerminalPrivacyResult = false;
    const revoke = () => {
      lifetime.abort();
      // Chrome 152 breaks the returned result when a terminal privacy action
      // unregisters itself in the MutationObserver microtask. Application access
      // is revoked immediately; only a completed, synchronous privacy close or
      // review-only privacy open waits one task for native result delivery.
      // Pending operations and every other tool still unregister immediately.
      if (deliveringTerminalPrivacyResult) setTimeout(() => nativeRegistration.abort(), 0);
      else nativeRegistration.abort();
    };
    this.registrations.set(name, revoke);
    const risk = WEBMCP_ACTIONS[binding.action];
    const result = (value: WebMcpResult) => ({ ...value, summary: this.copy[value.status], route: this.route,
      confirmationRequired: value.status === "review_required", receiptId: value.receiptId ?? null });
    const tool: NativeTool = {
      name, description: this.copy.actions[binding.action],
      inputSchema: z.toJSONSchema(binding.schema) as Record<string, unknown>,
      annotations: { readOnlyHint: risk === "R0", consequentialHint: risk === "R2" || risk === "R3" },
      execute: async (input, context) => {
        const started = Date.now();
        const finish = (value: WebMcpResult) => {
          const elapsed = Date.now() - started;
          try { this.observe({ action: binding.action, risk, outcome: value.status, duration: elapsed < 1000 ? "lt_1s" : elapsed < 10000 ? "1_to_10s" : "over_10s" }); } catch { /* Telemetry must never affect actions. */ }
          return result(value);
        };
        // The policy is read at execution time too: a stale native handle cannot act
        // during the microtask between a blocking DOM change and its observer refresh.
        if (lifetime.signal.aborted || this.disposed || !this.isCurrent() || !this.actionAllowed(binding.action)) return result({ status: "unavailable" });
        if (this.busy) return result({ status: "unavailable", code: "action_in_progress" });
        const parsed = binding.schema.safeParse(input);
        if (!parsed.success) return finish({ status: "validation_error" });
        const nativeSignal = callbackSignal(context);
        // Some current native implementations omit the documented callback context.
        // In that case the registration lifetime remains the sole cancellation
        // channel; this never represents a missing native signal as one. Route,
        // auth, consent, and disposal cleanup still abort that lifetime.
        const signal = nativeSignal ? AbortSignal.any([lifetime.signal, nativeSignal]) : lifetime.signal;
        if (signal.aborted) return result({ status: "cancelled" });
        this.busy = true;
        try {
          if (risk === "R2") {
            const details = binding.preview?.(parsed.data) ?? JSON.stringify(parsed.data, null, 2);
            if (!await this.confirm(this.copy.actions[binding.action], details.slice(0, 4000), signal)) return finish({ status: "cancelled" });
          }
          if (signal.aborted || !this.isCurrent() || !this.actionAllowed(binding.action)) return finish({ status: "cancelled" });
          const complete = (value: WebMcpResult) => {
            if (signal.aborted) return finish({ status: "cancelled" });
            // High-risk adapters can prepare UI, never claim an agent submitted it.
            if (risk === "R3" && value.status === "success") return finish({ ...value, status: "review_required", receiptId: null });
            return finish(value);
          };
          const execution: WebMcpResult | PromiseLike<WebMcpResult> = binding.execute(parsed.data, { signal });
          // DOM observers run in a microtask. A synchronous UI action may legitimately
          // change the context and trigger a runtime rebuild after it has completed;
          // do not turn that completed action into a contradictory cancellation. Real
          // asynchronous work (including Promise-like results) remains abortable.
          if (isThenable(execution)) {
            const value = await execution;
            if (signal.aborted || !this.isCurrent() || !this.actionAllowed(binding.action)) return finish({ status: "cancelled" });
            return complete(value);
          }
          const completed = complete(execution);
          if (((binding.action === "close_privacy_dialog" && execution.status === "success")
            || (binding.action === "set_consent_preferences" && execution.status === "review_required")) && !signal.aborted) {
            deliveringTerminalPrivacyResult = true;
            setTimeout(() => { deliveringTerminalPrivacyResult = false; }, 0);
          }
          return completed;
        } catch {
          return finish({ status: signal.aborted ? "cancelled" : "failure" });
        } finally { this.busy = false; }
      },
    };
    const previous = this.pending.get(name) ?? Promise.resolve();
    const registration = previous.catch(() => {}).then(async () => {
      if (!lifetime.signal.aborted && !this.disposed) await this.model.registerTool(tool, { signal: nativeRegistration.signal });
    }).catch(() => {
      revoke();
      if (this.registrations.get(name) === revoke) this.registrations.delete(name);
    });
    this.pending.set(name, registration);
    void registration.finally(() => { if (this.pending.get(name) === registration) this.pending.delete(name); });
    return () => {
      revoke();
      if (this.registrations.get(name) === revoke) this.registrations.delete(name);
    };
  }
  dispose() {
    this.disposed = true;
    for (const revoke of this.registrations.values()) revoke();
    this.registrations.clear();
  }
}
